import React, { useState, createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, DownloadIcon, ZoomInIcon, ZoomOutIcon, PrinterIcon } from 'lucide-react';
import { useTheme } from '../providers/ThemeProvider';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
export const PolonaiseInFViewer: React.FC = () => {
  const navigate = useNavigate();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const [zoom, setZoom] = useState(100);
  // Detect mobile device
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  // PDF URL for Polonaise in F Major
  const pdfUrl = 'https://www.mutopiaproject.org/ftp/BachJS/BWV117a/BWV-117a/BWV-117a-let.pdf';
  // Use mobile-friendly PDF parameters on mobile devices
  const pdfViewUrl = isMobile ? `${pdfUrl}#view=Fit&zoom=page-width` : `${pdfUrl}#view=FitH`;
  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 10, 200));
  };
  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 10, 50));
  };
  const handleDownload = () => {
    // Download the actual PDF
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = 'Bach - Polonaise in F Major.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handlePrint = () => {
    window.print();
  };
  return <div className={`min-h-screen ${isDark ? 'bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50]' : 'bg-gray-50'}`} data-id="element-6196">
      <Navbar data-id="element-6197" />

      {/* Wrapper with top padding to account for fixed Navbar */}
      <div className="pt-20" data-id="element-6198">
        {/* Hero Banner with Image */}
        <div className="relative w-full" style={{
        minHeight: '320px'
      }} data-id="element-6199">
          <img src="https://images.unsplash.com/photo-1465847899084-d164df4dedc6?w=400&h=300&fit=crop" alt="Polonaise in F Major" className="w-full h-64 md:h-80 object-cover" data-id="element-6200" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-black/70" data-id="element-6201" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8" data-id="element-6202">
            <div className="max-w-7xl mx-auto" data-id="element-6203">
              <button onClick={() => navigate(-1)} className="mb-4 flex items-center text-white/90 hover:text-white transition-colors" data-id="element-6204">
                <ArrowLeftIcon className="h-5 w-5 mr-2" data-id="element-6205" />
                Back to Library
              </button>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2" data-id="element-6206">
                Polonaise in F Major
              </h1>
              <p className="text-lg md:text-xl text-white/90" data-id="element-6207">
                BWV Anh. 117a, from the Anna Magdalena Notebook
              </p>
              <p className="text-base md:text-lg text-white/80 mt-1" data-id="element-6208">
                Johann Sebastian Bach
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className={`sticky top-20 z-10 ${isDark ? 'bg-navy-800/95 backdrop-blur-sm border-b border-white/10' : 'bg-white/95 backdrop-blur-sm border-b border-gray-200'} shadow-sm`} data-id="element-6209">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between" data-id="element-6210">
          <div className="flex items-center space-x-2" data-id="element-6211">
            {!isMobile && <>
                <button onClick={handleZoomOut} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'}`} aria-label="Zoom out" data-id="element-6212">
                  <ZoomOutIcon className="h-5 w-5" data-id="element-6213" />
                </button>
                <span className={`text-sm font-medium px-3 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-6214">
                  {zoom}%
                </span>
                <button onClick={handleZoomIn} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'}`} aria-label="Zoom in" data-id="element-6215">
                  <ZoomInIcon className="h-5 w-5" data-id="element-6216" />
                </button>
              </>}
          </div>
          <div className="flex items-center space-x-2" data-id="element-6217">
            <button onClick={handlePrint} className={`flex items-center justify-center px-4 py-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white border border-navy-700' : 'hover:bg-gray-100 text-gray-800 border border-gray-200'}`} data-id="element-6218">
              <PrinterIcon className="h-4 w-4 sm:mr-2" data-id="element-6219" />
              <span className="hidden sm:inline" data-id="element-6220">Print</span>
            </button>
            <button onClick={handleDownload} className="flex items-center justify-center px-4 py-2 border-2 border-green-500 text-green-500 bg-transparent hover:bg-green-500/10 rounded-lg transition-colors" data-id="element-6221">
              <DownloadIcon className="h-4 w-4 sm:mr-2" data-id="element-6222" />
              <span className="hidden sm:inline" data-id="element-6223">Download PDF</span>
            </button>
          </div>
        </div>
      </div>

      {/* Sheet Music Viewer */}
      <main className="max-w-7xl mx-auto px-4 py-8" data-id="element-6224">
        <div className={`rounded-xl overflow-hidden shadow-lg ${isDark ? 'bg-navy-800' : 'bg-white'}`} data-id="element-6225">
          {/* PDF Viewer - Embedded */}
          <div className="w-full" style={{
          height: 'calc(100vh - 300px)',
          minHeight: '600px'
        }} data-id="element-6226">
            <iframe src={pdfViewUrl} className="w-full h-full" title="Polonaise in F Major Sheet Music" style={{
            border: 'none',
            ...(isMobile ? {} : {
              transform: `scale(${zoom / 100})`,
              transformOrigin: 'top center',
              transition: 'transform 0.2s ease'
            })
          }} data-id="element-6227" />
          </div>
        </div>

        {/* Piece Information */}
        <div className={`mt-8 rounded-xl p-6 ${isDark ? 'bg-navy-800' : 'bg-white'} shadow-sm`} data-id="element-6228">
          <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-6229">
            About This Piece
          </h2>
          <div className="grid md:grid-cols-2 gap-6" data-id="element-6230">
            <div data-id="element-6231">
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-6232">
                Composition Details
              </h3>
              <dl className="space-y-2" data-id="element-6233">
                <div data-id="element-6234">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-6235">
                    Composer
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-6236">
                    Johann Sebastian Bach
                  </dd>
                </div>
                <div data-id="element-6237">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-6238">
                    Year Composed
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-6239">
                    c. 1725
                  </dd>
                </div>
                <div data-id="element-6240">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-6241">
                    Key
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-6242">
                    F Major
                  </dd>
                </div>
                <div data-id="element-6243">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-6244">
                    Catalog
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-6245">
                    BWV Anh. 117a
                  </dd>
                </div>
              </dl>
            </div>
            <div data-id="element-6246">
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-6247">
                Performance Notes
              </h3>
              <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-6248">
                This charming polonaise comes from the Anna Magdalena Notebook,
                a collection of keyboard pieces compiled by Bach for his second
                wife. The piece features the characteristic rhythm of a
                polonaise dance, with its distinctive dotted rhythm and emphasis
                on the first beat. Perfect for developing a graceful, dance-like
                character in playing, this work exemplifies the pedagogical
                approach Bach took in teaching keyboard technique through
                musically engaging pieces.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer data-id="element-6249" />
    </div>;
};